import React, { memo } from "react";
import { css, useTheme } from "@emotion/react";

import ProgressBar from "core/Components/Graphs/ProgressBar";
import { useAppState } from "core/contexts/AppContext";
import { CATALOGS_WIDTH } from "elements/Components/SegmentedVideo/includes/consts";
import VideoCatalogItemContainer from "elements/Components/SegmentedVideo/components/VideoCatalogItemContainer";
import VideoCatalogItem from "elements/Components/SegmentedVideo/components/VideoCatalogItem";
import VideoCatalogContainer from "elements/Components/SegmentedVideo/components/VideoCatalogContainer";

type Props = {
    videoItems?: SegmentedVideoItem[],
    currentVideoItem: SegmentedVideoItem,
    colour: string,
    totalVideoCount: number,
    watchedVideoCount: number,
    watchedVideoPercent: number,
    onChangeCurrentTopic: (urlSegment: string) => void,
};

const VideoCatalogs: React.FC<Props> = ({
    videoItems = [],
    currentVideoItem,
    colour,
    totalVideoCount,
    watchedVideoCount,
    watchedVideoPercent,
    onChangeCurrentTopic,
}) => {
    const theme = useTheme();
    const [appState] = useAppState();
    const isLoggedIn = !!appState.user?.id;

    const style = css`
        display: flex;
        flex-direction: column;
        width: ${CATALOGS_WIDTH}px;
    `;

    const progressContainerStyle = css`
        width: 100%;
        height: 44px;
        font-size: 13px;
        display: flex;
        align-items: center;
    `;

    const graphBoxStyle = css`
        width: 70%;
        margin-left: 39px;
        display: flex;
        align-items: center;

        ${theme.breakpoints.down("md")} {
            width: 100%;
            margin-left: 11px;
        }
    `;

    const progressBarStyle = css`
        &:after {
            background-color: ${colour};
        }
    `;

    const graphValueStyle = css`
        width: 25%;
        text-align: center;

        ${theme.breakpoints.between("sm", "md")} {
            width: 12%;
        }
    `;

    const videoCatalogItemContainerStyle = css`
        width: 100%;
        padding: 25px 22px 0 50px;
    `;

    return (
        <VideoCatalogContainer css={style}>
            {isLoggedIn && (
                <div css={progressContainerStyle}>
                    <div css={graphBoxStyle}>
                        <ProgressBar percent={watchedVideoPercent} css={progressBarStyle} />
                    </div>
                    <div css={graphValueStyle}>{watchedVideoCount} of {totalVideoCount}</div>
                </div>
            )}
            <VideoCatalogItemContainer css={videoCatalogItemContainerStyle}>
                {videoItems.map((videoItem: SegmentedVideoItem) => {
                    const isCurrent = videoItem.id === currentVideoItem.id;
                    const isWatched = videoItem.isWatched;
                    const isLast = videoItem.id === videoItems[videoItems.length - 1].id;

                    const onClickHandler = () => {
                        onChangeCurrentTopic(videoItem.urlSegment);
                    };

                    return (
                        <VideoCatalogItem
                            key={`video-item-${videoItem.id}`}
                            title={videoItem.title}
                            colour={colour}
                            isCurrent={isCurrent}
                            isLast={isLast}
                            isWatched={isWatched}
                            onClickHandler={onClickHandler}
                        />
                    );
                })}
            </VideoCatalogItemContainer>
        </VideoCatalogContainer>
    );
};

export default memo(VideoCatalogs);